import * as React from 'react';
import { connect } from 'react-redux';
import sanitizeHtml from 'sanitize-html';
import isEmpty from 'lodash/isEmpty';

import ActionButton from 'components/buttons/action-button/ActionButton';
import RadioButtons from 'components/inputs/radio-buttons/RadioButtons';
import { ModalFrame, ModalFrameFooter, ModalFrameInner, ModalFrameMessage } from 'components/modal';

import { removeInfluencerProfile } from 'modules/influencers/reducers/influencerProfile';
import { fetchBrandsThatHaveAddedNotes, resetBrandsThatHaveAddedNotes } from 'modules/notes/reducers/notes';
import { deleteProfileViaSearch } from 'modules/discovery/creators/reducers/creators';

import { __t, applyTranslations } from 'utils/language';
import { INFLUENCER_LIGHTWEIGHT_DELETION_REASON } from 'utils/influencers';

import './influencer-delete-modal.scss';

import type { InfluencerProfile, NoteBrandListItem } from 'types';

applyTranslations(require('../influencer-delete-button.i18n.json'));

type OwnProps = {
  influencer: InfluencerProfile,
  onCloseModal: () => void,
  onDeleteProfile?: () => void
};

type StateProps = {
  brandsThatHaveAddedNotes: Array<NoteBrandListItem>,
  isLoading: boolean
};

type DispatchProps = {
  dispatchDeleteInfluencerProfile: (profileId: number, reason: string) => Promise<any>,
  dispatchFetchBrandsThatHaveAddedNotes: (influencerId: number) => Promise<void>,
  dispatchResetBrandsThatHaveAddedNotes: () => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  reason: string
};

const SANITIZE_HTML_CONFIG = {
  allowedTags: ['div', 'span', 'a'],
  allowedAttributes: {
    a: ['href', 'target', 'rel']
  }
};

class InfluencerDeleteModal extends React.PureComponent<Props, State> {
  static defaultProps = {
    onDeleteProfile: () => {}
  };

  state = {
    reason: INFLUENCER_LIGHTWEIGHT_DELETION_REASON[0]
  };

  componentDidMount(): void {
    const { influencer, dispatchFetchBrandsThatHaveAddedNotes } = this.props;

    if (influencer) {
      dispatchFetchBrandsThatHaveAddedNotes(influencer.id);
    }
  }

  componentWillUnmount(): void {
    const { dispatchResetBrandsThatHaveAddedNotes } = this.props;

    dispatchResetBrandsThatHaveAddedNotes();
  }

  deleteProfile = () => {
    const { influencer, dispatchDeleteInfluencerProfile, onCloseModal, onDeleteProfile } = this.props;
    const { reason } = this.state;

    dispatchDeleteInfluencerProfile(influencer.id, reason)
      .then(onCloseModal)
      .then(onDeleteProfile);
  };

  handleReasonChange = (reason: string) => {
    this.setState({
      reason
    });
  };

  getOptions() {
    return INFLUENCER_LIGHTWEIGHT_DELETION_REASON.map(reason => ({
      value: reason,
      name: __t(`influencer_delete_button.deletion_reasons.${reason}`)
    }));
  }

  getBrandLink() {
    const { brandsThatHaveAddedNotes } = this.props;

    return brandsThatHaveAddedNotes
      .map(item => (`
        <a
          href="/organizations/${item.organizationId}/brand/settings"
          target="_blank"
          rel="noopener noreferrer"
        >${item.name}</a>`
      ));
  }

  render() {
    const { brandsThatHaveAddedNotes, isLoading } = this.props;
    const { reason } = this.state;

    return (
      <ModalFrame
        data={{}}
        title={__t('influencer_delete_button.delete_button')}
      >
        <ModalFrameInner narrowContent>
          <React.Fragment>
            <ModalFrameMessage message={__t('influencer_delete_button.deletion_warning')} />
            <ModalFrameMessage message={__t('influencer_delete_button.deletion_reasons_title')} />
            <RadioButtons
              selected={reason}
              options={this.getOptions()}
              onChange={this.handleReasonChange}
            />
            {
              !isEmpty(brandsThatHaveAddedNotes) && (
                <div
                  className="influencer-delete-modal__alert"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(
                      __t('influencer_delete_button.contains_notes', {
                        brandName: this.getBrandLink()
                      }),
                      SANITIZE_HTML_CONFIG
                    )
                  }}
                />
              )
            }
            <ModalFrameFooter>
              <ActionButton
                color="secondary"
                width="normal"
                disabled={isLoading}
                onClick={this.deleteProfile}
              >
                {__t('influencer_delete_button.delete_button') }
              </ActionButton>
            </ModalFrameFooter>
          </React.Fragment>
        </ModalFrameInner>
      </ModalFrame>
    );
  }
}

const mapStateToProps = ({ notes }): StateProps => ({
  brandsThatHaveAddedNotes: notes.brandList,
  isLoading: notes.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDeleteInfluencerProfile: (profileId, reason) => (
    dispatch(removeInfluencerProfile(profileId, reason))
      .then(() => dispatch(deleteProfileViaSearch(profileId)))
  ),
  dispatchFetchBrandsThatHaveAddedNotes: (influencerId: number) => (
    dispatch(fetchBrandsThatHaveAddedNotes(influencerId))
  ),
  dispatchResetBrandsThatHaveAddedNotes: () => (
    dispatch(resetBrandsThatHaveAddedNotes())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(InfluencerDeleteModal);
